import { authFetch, BASE_BACKEND_URL } from '../../auth'
import React, { useState, useEffect, createContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const ClientContext = createContext()

const normalizeBackendMessagePart = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'string') {
    const trimmedValue = value.trim()
    return trimmedValue || null
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => normalizeBackendMessagePart(item))
      .filter(Boolean)
      .join(' ')
  }

  if (typeof value === 'object') {
    if (typeof value.message === 'string' && value.message.trim()) {
      return value.message.trim()
    }

    if (typeof value.error === 'string' && value.error.trim()) {
      return value.error.trim()
    }

    try {
      return JSON.stringify(value)
    } catch (error) {
      return String(value)
    }
  }

  return String(value)
}

const ClientProvider = ({ children }) => {
  const navigate = useNavigate()
  const params = useParams()
  const [isFetching, setIsFetching] = useState(true)
  const [isEditClient, setIsEditClient] = useState(false)
  const [clientDetailsData, setClientDetailsData] = useState(null)
  const [clientEditDetailsData, setClientEditDetailsData] = useState('')
  const [editingClient, setEditingClient] = useState(false)
  const [addingClient, setAddingClient] = useState(false)
  const [failedCreation, setFailedCreation] = useState(false)
  const [failedCreationInfo, setFailedCreationInfo] = useState('')
  const [activeTab, setActiveTab] = useState('Detalles')
  const [clientForm, setClientForm] = useState({
    client_group_id: '',
    sales_channel_id: '',
    payment_type_id: '',
    credit_term_id: '',
    province_id: '',
    district_id: '',
    town_id: '',
    price_list_id: '',
    price_list_name: '',
    db_ref: '',
    active: '',
    taxpayer_id: '',
    taxpayer_verificator_digit: '',
    business_name: '',
    commercial_name: '',
    customer_name_db_ref: '',
    credit_limit: '',
    legal_representative: '',
    billing_contact_name: '',
    billing_contact_email: '',
    billing_contact_phone: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    city: '',
    street: '',
    location_description: '',
    geographical_latitude: '',
    geographical_longitud: '',
    is_cash_client: undefined
  })

  const urlParts = window.location.href.split('/')
  const id = params?.id

  useEffect(() => {
    fetchDetailsFromServer()
  }, [])

  const fetchDetailsFromServer = () => {
    setIsFetching(true)

    authFetch(`${BASE_BACKEND_URL}/clients/${id}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => {
        setClientDetailsData(data)
        setIsFetching(false)
        setDefaultParams(data)
      })
      .catch((e) => console.log(e))

    fetchDataForEdit()
  }

  const setDefaultParams = (data) => {
    setClientForm({
      client_group_id: data['client_group'] ? data['client_group']['id'] : '',
      sales_channel_id: data['sales_channel'] ? data['sales_channel']['id'] : '',
      payment_type_id: data['payment_type'] ? data['payment_type']['id'] : '',
      credit_term_id: data['credit_term'] ? data['credit_term']['id'] : '',
      province_id: data['province'] ? data['province']['id'] : '',
      district_id: data['district'] ? data['district']['id'] : '',
      town_id: data['town'] ? data['town']['id'] : '',
      price_list_id: data['price_list'] ? data['price_list']['id'] : '',
      price_list_name: data['price_list'] ? data['price_list']['db_ref'] : '',
      db_ref: data['db_ref'],
      active: data['active'],
      taxpayer_id: data['taxpayer_id'],
      taxpayer_verificator_digit: data['taxpayer_verificator_digit'],
      business_name: data['business_name'],
      commercial_name: data['commercial_name'],
      customer_name_db_ref: data['customer_name_db_ref'],
      credit_limit: data['credit_limit'],
      legal_representative: data['legal_representative'],
      billing_contact_name: data['billing_contact_name'],
      billing_contact_email: data['billing_contact_email'],
      billing_contact_phone: data['billing_contact_phone'],
      contact_name: data['contact_name'],
      contact_email: data['contact_email'],
      contact_phone: data['contact_phone'],
      city: data['city'],
      street: data['street'],
      location_description: data['location_description'],
      geographical_latitude: data['geographical_latitude'],
      geographical_longitud: data['geographical_longitud'],
      is_cash_client: data.is_cash_client
    })
  }

  const fetchDataForEdit = () => {
    authFetch(`${BASE_BACKEND_URL}/clients/new`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then((response) => {
        if (!response) return
        return response.json()
      })
      .then((data) => setClientEditDetailsData(data))
      .catch((e) => console.log(e))
  }

  const createOrUpdateClient = (event) => {
    if (event.target.value === 'Actualizar cliente') {
      setIsEditClient(true)
      setEditingClient(true)
      passEditState(true)
    } else if (event.target.value === 'Cancelar') {
      setIsEditClient(false)
      setEditingClient(false)
      passEditState(false)
    }
  }

  const passEditState = (val) => {
    setIsEditClient(val)
  }

  const handleSubmitEditClient = (event) => {
    event.preventDefault()

    authFetch(`${BASE_BACKEND_URL}/clients/${id}`, {
      method: 'PUT',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(clientForm)
    })
      .then((r) => {
        if (!r) return
        return r.json().then((data) => ({ status: r.status, body: data }))
      })
      .then((obj) => evaluateResponse(obj))
      .catch((e) => console.log(e))
  }

  const evaluateResponse = (obj) => {
    const backendMessage = [
      ...(Array.isArray(obj?.body?.errors) ? obj.body.errors : []),
      obj?.body?.error,
      obj?.body?.message
    ]
      .map((messagePart) => normalizeBackendMessagePart(messagePart))
      .filter(Boolean)
      .join(' ')

    switch (obj.status) {
      case 422:
        setFailedCreationInfo(backendMessage || 'Verifique la informacion ingresada.')
        setFailedCreation(true)
        break
      case 409:
        setFailedCreationInfo(
          backendMessage ||
            'No se puede crear cliente. Código ya esta en uso. Verifique los datos del cliente'
        )
        setFailedCreation(true)
        break
      case 404:
        setFailedCreationInfo(backendMessage || 'Servidor fuera de servicio')
        setFailedCreation(true)
        break
      case 500:
        setFailedCreationInfo(backendMessage || 'Error interno en el sistema')
        setFailedCreation(true)
        break
      default:
        const data = obj.body
        setIsEditClient(false)
        setEditingClient(false)
        setFailedCreation(false)
        setFailedCreationInfo('')
        fetchDetailsFromServer()
        navigate(`/clientes/${data['id']}/detalles`)
        break
    }
  }

  const handleChange = (event) => {
    const { name, value, options, selectedIndex } = event.target

    if (name === 'payment_type_selection') {
      setClientForm({
        ...clientForm,
        is_cash_client: value === 'contado'
      })
      return
    }

    if (name === 'price_list_id') {
      const selectedOption = options[selectedIndex]
      const selectedDbRef = selectedOption?.getAttribute('data-db_ref') || ''

      setClientForm({
        ...clientForm,
        price_list_id: value,
        price_list_name: selectedDbRef
      })
      return
    }

    setClientForm({
      ...clientForm,
      [name]: value
    })
  }

  return (
    <ClientContext.Provider
      value={{
        clientData: clientDetailsData,
        clientEditDetailsData: clientEditDetailsData,
        isEditClient: isEditClient,
        isFetching: isFetching,
        failedCreation: failedCreation,
        failedCreationInfo: failedCreationInfo,
        handleChange: handleChange,
        editingClient: editingClient,
        handleSubmitEditClient: handleSubmitEditClient,
        activeTab: activeTab,
        passEditState: passEditState,
        handleClickButton: createOrUpdateClient
      }}
    >
      {children}
    </ClientContext.Provider>
  )
}

const ClientConsumer = ClientContext.Consumer

export { ClientProvider, ClientConsumer, ClientContext }
